import { useQuery } from '@tanstack/react-query';
import { type AvailableLayer, getAvailableLayers } from '~/api/availableLayers';

/** Query key for the org's available-layer catalog. Shared with the retry affordance
 * (`useLayerConfigLoadState`) so the catalog can be refetched without duplicating the key. */
export const availableLayersQueryKey = (organizationId: string | undefined) =>
  ['availableLayers', { organizationId }] as const;

/**
 * The org's available-layer catalog (`GET /v1/availableLayers`, CG-606) as a React Query
 * — `AvailableLayer[]` with loading/error (CG-510 D1).
 *
 * Cached for the whole session (`staleTime`/`gcTime` Infinity): the catalog is static
 * within a session and scoped by org via the query key, so it is fetched once per org
 * and never refetched in the background. Disabled until an org is resolved.
 */
export function useAvailableLayers(organizationId: string | undefined) {
  return useQuery<AvailableLayer[]>({
    enabled: !!organizationId,
    queryKey: availableLayersQueryKey(organizationId),
    queryFn: () => {
      // `enabled` gates on the org; the throw covers the unreachable manual-refetch case.
      if (!organizationId) throw new Error('useAvailableLayers: no organization resolved');
      return getAvailableLayers(organizationId);
    },
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    refetchOnWindowFocus: false,
  });
}
